import "./Table.scss";

import { Transition } from "@headlessui/react";
import clsx from "clsx";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { ScrollIndicatorContainer } from "@/components/Layout/ScrollIndicatorContainer/ScrollIndicatorContainer";
import { TableEmptyState } from "@/components/Layout/Table/TableEmptyState/TableEmptyState";

interface Props {
  /** Set on the `<table>` element itself. */
  dataTestid?: string;
  headline?: string;
  /** Omit to render no pagination — e.g. when the caller renders its own. */
  paginationPages?: number;
  noRows?: boolean;
  /** Defaults to `cockpit.deviceList.noSearchResults` when `noRows` is true. */
  noRowsMessage?: string;
  variant?: "plain" | "tile";
  hasPaddingBottom?: boolean;
  isLoading?: boolean;
}

export const Table: FCC<Props> = ({
  children,
  dataTestid,
  headline,
  paginationPages = 0,
  noRows = false,
  noRowsMessage,
  variant = "tile",
  hasPaddingBottom = true,
  isLoading = false,
}) => {
  const headlineId = useId();
  const { t } = useTranslation("portal");

  return (
    <Transition
      as="div"
      data-testid="table-box"
      className={clsx("table-box", variant === "plain" && "table-box--plain")}
      appear={true}
      show={true}
      enter="transition-opacity ease-linear duration-150"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity ease-linear duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div>
        {headline && (
          <div className="table-box-header">
            <h2
              id={headlineId}
              className="text-headline-sm font-semibold leading-md"
            >
              {headline}
            </h2>
          </div>
        )}
        {/* Figma: the empty message sits above the scroll indicator. */}
        <ScrollIndicatorContainer
          trailingContent={
            noRows && (
              <TableEmptyState
                message={
                  noRowsMessage ?? t("cockpit.deviceList.noSearchResults")
                }
              />
            )
          }
        >
          <div className={clsx("relative", isLoading && "min-h-23")}>
            <table
              data-testid={dataTestid}
              className={clsx("table", isLoading && "opacity-50")}
              aria-labelledby={headline ? headlineId : undefined}
            >
              {children}
            </table>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center pt-14">
                <LocalLoadingSpinner />
              </div>
            )}
          </div>
        </ScrollIndicatorContainer>
      </div>
      {paginationPages > 1 ? (
        <Pagination paginationPages={paginationPages} />
      ) : (
        hasPaddingBottom && <div className="pb-10" />
      )}
    </Transition>
  );
};
